import React, { useEffect } from 'react';
import { generateResolver, yup, Person } from 'dyl-components';
import { Button, Form } from 'semantic-ui-react';
import { useForm, Controller } from 'react-hook-form';
import ConvertToCustomerFields from "./ConvertToCustomerFields";
import accountActions from 'actions/account';
import { useSelector } from "react-redux";
import campaignsAction from "actions/campaigns";
import useConvertToCustomer from './useConvertToCustomer';

import "./ConvertToCustomerForm.scss";

const ConvertAccountToCustomerForm = ({
    onClose,
    size,
    onModify,
    pipeline_id,
    refresh,
    existing_account_id,
    owner_id,
    current_primary_contact_id
}) => {
    const { formState: { isDirty, isValid }, control, handleSubmit, watch } = useForm({
        mode: 'onChange',
        defaultValues: {
            pipeline: null,
            stage: null,
            business_model: null,
            owner_id: owner_id || "",
        },
        resolver: generateResolver({
            owner_id: yup.string().required('This field is required')
        })
    });

    const watchedOwnerId = watch("owner_id");

    useEffect(() => {
        onModify(isDirty);
    }, [isDirty, onModify]);

    const { isCreating, createCustomer, dispatch } = useConvertToCustomer({ onClose, refresh, pipeline_id });

    const { primaryContactOptions, isReadingPrimaryContactOptions } = useSelector(state => ({
        primaryContactOptions: state.account.contactsForPinning.map(contact => {
            const name = `${contact.first_name} ${contact.last_name}`;

            return ({
                key: contact.id,
                value: contact.id,
                text: name,
                content: (
                    <Person username={name} subtitleLabel='Job Title' email={contact?.job_title || ''} />
                ),
                pinned: contact.pinned
            });
        }),
        isReadingPrimaryContactOptions: state.account.isReadingContactsForPinning,
    }));

    useEffect(() => {
        dispatch(accountActions.readContactsForPinning(existing_account_id, { page: 1, limit: 100 }));
    }, [existing_account_id, dispatch]);

    useEffect(() => {
        dispatch(campaignsAction.readPersonCampaignConverted({ person_id: owner_id, campaign_conversion: "customer" }));
    }, [owner_id, dispatch]);

    const onCreate = async (data) => {
        try {
            const account_id = Number(existing_account_id);
            const primaryContactWillBeReplaced = owner_id !== data.owner_id;
            
            if (primaryContactWillBeReplaced || !current_primary_contact_id) {
                await dispatch(accountActions.updateAccount(account_id, {
                    pinned: primaryContactOptions.filter(option => option.pinned && option.value !== Number(data.owner_id)).map(option => option.value),
                    primary_person_id: Number(data.owner_id)
                }))
            }

            await createCustomer({ 
                account_id, 
                owner_id: data.owner_id,
                data 
            });

        } catch (e) {
            console.log(e);
        }
    }

    return (
        <>
            <Form loading={isCreating} disabled={isCreating} size={size} noValidate className='ConvertToCustomerForm'>
                <Form.Group widths={'equal'}>
                    <Controller
                        name={'owner_id'}
                        control={control}
                        render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                            <Form.Dropdown
                                label={'Primary Contact'}
                                placeholder='Select Primary Contact'
                                selectOnBlur={false}
                                selection
                                options={primaryContactOptions}
                                value={value}
                                onChange={(_, { value: contact_id }) => {
                                    dispatch(campaignsAction.readPersonCampaignConverted({ person_id: contact_id, campaign_conversion: "customer" }))
                                    onChange({ target: { name, value: contact_id } });
                                }}
                                compact
                                clearable
                                loading={isReadingPrimaryContactOptions}
                                required
                                error={error?.message}
                            />
                        )}
                    />
                </Form.Group>
                <ConvertToCustomerFields control={control} columns={2} pipelineName={'pipeline'} stageName={'stage'} typeName={'business_model'} isEmpty={watchedOwnerId === ''} />
            </Form>
            <Button floated='right' disabled={!isValid || isCreating || isReadingPrimaryContactOptions} primary onClick={handleSubmit(onCreate)} loading={isCreating || isReadingPrimaryContactOptions}>Save</Button>
        </>
    )
}

export default ConvertAccountToCustomerForm;
